import { MULTIPART_PRESIGN_BATCH_SIZE } from '@atlas/types'
import type { UploadPartPresign } from '@/services/file-multipart.service'

/** Renew a URL once 80% of its returned validity has elapsed locally, before scheduling the part. */
const RENEWAL_THRESHOLD = 0.8

interface HeldUrl {
	uploadUrl: string
	validForSeconds: number
	/** Local monotonic-ish receipt time. Only ever compared against another reading of the same clock. */
	receivedAt: number
}

/**
 * Demand-driven presigned-URL supply for one file's parts.
 *
 * Two rules define it, and both are about not signing what will not be used:
 * a batch is requested only when the scheduler actually needs a part it does
 * not hold, and at most `MULTIPART_PRESIGN_BATCH_SIZE` not-yet-started URLs are
 * held at a time. Renewal is decided from locally measured elapsed time against
 * the backend's `validForSeconds`, so a browser clock that disagrees with the
 * server changes nothing.
 */
export class PartUrlProvider {
	private held = new Map<number, HeldUrl>()

	constructor(
		private readonly request: (partNumbers: number[]) => Promise<UploadPartPresign[]>,
		private readonly now: () => number = () => Date.now(),
	) {}

	/** How many not-yet-started URLs are currently held — the batch bound is asserted against this. */
	get heldCount(): number {
		return this.held.size
	}

	/**
	 * A URL usable right now for `partNumber`, requesting a batch only if there
	 * is no fresh one held. `upcoming` lets one round trip cover the parts the
	 * scheduler is about to ask for, which is what makes batching worthwhile
	 * without ever presigning the whole file.
	 */
	async urlFor(partNumber: number, upcoming: number[] = []): Promise<string> {
		const fresh = this.freshUrl(partNumber)
		if (fresh) return fresh

		const batch = this.composeBatch(partNumber, upcoming)
		const presigned = await this.request(batch)
		for (const part of presigned) {
			this.held.set(part.partNumber, {
				uploadUrl: part.uploadUrl,
				validForSeconds: part.validForSeconds,
				receivedAt: this.now(),
			})
		}
		const acquired = this.freshUrl(partNumber)
		if (!acquired) throw new Error(`No presigned URL was returned for part ${partNumber}`)
		return acquired
	}

	/** Records a URL the control plane already returned, so the first part needs no extra round trip. */
	seed(part: UploadPartPresign): void {
		this.held.set(part.partNumber, {
			uploadUrl: part.uploadUrl,
			validForSeconds: part.validForSeconds,
			receivedAt: this.now(),
		})
	}

	/** Discards a URL S3 refused, so the next request for that part re-presigns it rather than reusing the same signature. */
	discard(partNumber: number): void {
		this.held.delete(partNumber)
	}

	/** A part that has started transferring is no longer "not-yet-started" and stops counting against the batch bound. */
	consume(partNumber: number): void {
		this.held.delete(partNumber)
	}

	private freshUrl(partNumber: number): string | null {
		const held = this.held.get(partNumber)
		if (!held) return null
		const elapsedSeconds = (this.now() - held.receivedAt) / 1000
		if (elapsedSeconds >= RENEWAL_THRESHOLD * held.validForSeconds) {
			this.held.delete(partNumber)
			return null
		}
		return held.uploadUrl
	}

	/** The needed part first, then as many upcoming ones as the batch bound still allows. */
	private composeBatch(partNumber: number, upcoming: number[]): number[] {
		const room = Math.max(1, MULTIPART_PRESIGN_BATCH_SIZE - this.held.size)
		const batch = [partNumber]
		for (const candidate of upcoming) {
			if (batch.length >= room) break
			if (candidate === partNumber || this.held.has(candidate)) continue
			batch.push(candidate)
		}
		return batch
	}
}
